import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Package, Plus, Pencil, Share2, Trash2, ArrowLeft, Loader2, X, Save, Image as ImageIcon, ChevronDown, AlertTriangle, Search } from 'lucide-react';

import ShopAnalytics from './ShopAnalytics'; 
import { BarChart3 } from 'lucide-react'; // Add this for the analytics icon

interface ProductManagerProps {
  shopId: number;
  onBack: () => void;
}

interface Variant {
  id: string;
  variant_name: string;
  value: string;
  price_impact: number;
  variant_stock: number;
  variant_image_url?: string | null;
}

// Sub-component for individual product cards to handle local "selected variant" state
const ProductCard = ({ product, handleShare, handleDelete, handleAmend }: { product: any, handleShare: any, handleDelete: any, handleAmend: any }) => {
  const [selectedVariantId, setSelectedVariantId] = useState<string>('base');

  // Find the selected variant object
  const selectedVariant = product.variants?.find((v: any) => v.id === selectedVariantId);


  // 1. DYNAMIC PRICE CALCULATION (Prevents NaN)
  const basePrice = Number(product.base_price) || product.basePrice || 0;
  const impact = Number(selectedVariant?.price_impact) || selectedVariant?.priceImpact || 0;
  const displayPrice = basePrice + impact;
    
  // 2. DYNAMIC STOCK CALCULATION (Prevents Fixed 0)
  const displayStock = selectedVariant 
    ? (Number(selectedVariant.variant_stock) || selectedVariant.stock || 0) 
    : Number(product.stock_quantity || product.stock || product.quality || 0);

  const isLowStock = displayStock > 0 && displayStock <= 5;
  const isOutOfStock = displayStock <= 0;

  return (
    <div className="group bg-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-xl transition-all duration-300 flex flex-col">
      <div className="relative aspect-video bg-gray-100">
        {product.product_image_url ? (
          <img src={product.product_image_url} alt={product.product_name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-300"><ImageIcon className="w-12 h-12" /></div>
        )}
        <div className="absolute top-3 left-3 bg-white/90 backdrop-blur px-3 py-1 rounded-full text-xs font-bold text-blue-600 shadow-sm">{product.product_category}</div>
        
        {/* Low Stock Badge */}
        {isLowStock && (
          <div className="absolute top-3 right-3 bg-orange-500 text-white px-2 py-1 rounded-lg text-[10px] font-black flex items-center gap-1 animate-pulse shadow-lg">
            <AlertTriangle className="w-3 h-3" /> LOW STOCK
          </div>
        )}
      </div>
      
      <div className="p-5 flex-1 flex flex-col">
        <div className="flex justify-between items-start mb-1">
          <h3 className="font-bold text-gray-900 text-lg leading-tight">{product.product_name}</h3>
          <p className="text-blue-600 font-black text-lg">KShs {displayPrice}</p>
        </div>
        
        <p className="text-gray-500 text-xs mb-4 line-clamp-1 italic">{product.product_description}</p>

        {/* Dropdown Variant Selector (Matches your screenshot) */}
        <div className="space-y-1.5 mb-4">
          <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Select Variant:</label>
          <div className="relative">
            <select 
              value={selectedVariantId}
              onChange={(e) => setSelectedVariantId(e.target.value)}
              className="w-full pl-3 pr-10 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-bold appearance-none cursor-pointer hover:bg-blue-700 transition-colors focus:ring-2 focus:ring-blue-300 outline-none shadow-md"
            >
              <option value="base" className="bg-white text-gray-800 font-medium">
                Standard - KShs {basePrice} ({Number(product.stock_quantity) || 0} in stock)
              </option>
              {product.variants?.map((v: any) => {
                const vImpact = Number(v.price_impact || v.priceImpact || 0);
                const vStock = Number(v.variant_stock || v.stock || 0);
                return (
                  <option key={v.id} value={v.id} className="bg-white text-gray-800 font-medium">
                    {v.variant_name || 'Option'}: {v.value || ''} - KShs {basePrice + vImpact} ({vStock} in stock)
                  </option>
                );
              })}

            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white pointer-events-none" />
          </div>
        </div>
        
        <div className="mt-auto pt-4 flex items-center justify-between border-t border-gray-50">
          <div className="flex flex-col">
            <span className="text-[10px] text-gray-400 font-bold uppercase">Current Inventory</span>
            <span className={`text-sm font-black ${isOutOfStock ? 'text-red-500' : isLowStock ? 'text-orange-500' : 'text-green-600'}`}>
              {displayStock} Units Available
            </span>
          </div>

          
          <div className="flex gap-1">
            <button 
              onClick={() => handleAmend(product)} 
              className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
              title="Amend Product"
            >
              <Plus className="w-4 h-4" /> {/* You can change Plus to Pencil if imported */}
            </button>

            <button onClick={() => handleShare(product)} className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-all"><Share2 className="w-4 h-4" /></button>
            <button onClick={() => handleDelete(product.product_id)} className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"><Trash2 className="w-4 h-4" /></button>

          </div>
        </div>
      </div>
    </div>
  );
};
 

export const ProductManager: React.FC<ProductManagerProps> = ({ shopId, onBack }) => {

  // 1. WhatsApp QR State & Logic
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [isFetchingQr, setIsFetchingQr] = useState(false);

  const fetchWhatsAppQR = async () => {
    setIsFetchingQr(true);
    try {
      const response = await fetch('https://n8n.tenear.com/webhook/fetch-evolution-qrcode', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ shop_id: shopId, userId: user?.id })
      });
      
      const data = await response.json();

      const result = Array.isArray(data) ? data[0] : data;

      if (result && result.base64) {
        setQrCode(result.base64);
      } else {
        console.warn("Property 'base64' not found in response:", result);
    }

    } catch (error) {
      console.error("Error fetching QR:", error);
      alert("Failed to fetch QR code.");
    } finally {
      setIsFetchingQr(false);
    }
  };


  const [products, setProducts] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [shopName, setShopName] = useState<string>('');
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  
  const [showAnalytics, setShowAnalytics] = useState(false); // 2. Add toggle state

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);

  const [hasVariants, setHasVariants] = useState(false);
  const [variants, setVariants] = useState<Variant[]>([]);

  const [newProduct, setNewProduct] = useState({
    product_name: '',
    product_category: '',
    product_description: '',
    base_price: '',
    stock_quantity: '0',
    product_image_url: ''

  
 });

  // --- IMAGE LOGIC ---
  const handleFile = (file: File) => {
    setSelectedFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
    else if (e.type === "dragleave") setDragActive(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) handleFile(e.dataTransfer.files[0]);
  };

  const convertToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = error => reject(error);
    });
  };

  // --- VARIANT LOGIC ---
  const addVariantRow = () => {
    const id = Date.now().toString(36) + Math.random().toString(36).substring(2);
    setVariants([...variants, { id, variant_name: '', value: '', price_impact: 0, variant_stock: 0 }]);
  };

  const removeVariantRow = (id: string) => setVariants(variants.filter(v => v.id !== id));

  const updateVariant = (id: string, field: keyof Variant, val: any) => {
    setVariants(variants.map(v => v.id === id ? { ...v, [field]: val } : v));
  };

  const fetchProducts = async () => {
    setIsLoading(true);
    try {
      const response = await fetch('https://n8n.tenear.com/webhook/shop-products-management', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'get_products', shop_id: shopId })
      });
      const result = await response.json();

      if (Array.isArray(result) && result.length > 0) {
        setShopName(result[0].shop_name || '');
        setProducts(result);
      } else if (result.products) {
        setShopName(result.shop_name || '');
        setProducts(result.products);
      }
    } catch (error) { console.error("Fetch failed:", error); } finally { setIsLoading(false); }
  };


  useEffect(() => { 
    if (shopId) fetchProducts();
    return () => { if(imagePreview) URL.revokeObjectURL(imagePreview); }
  }, [shopId]);

  // Make sure this is AFTER your useState hooks but BEFORE the return statement
  const filteredProducts = (products || []).filter(p => 
    p.product_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.product_category?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAddProduct = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // 1. Create FormData object (required for binary/files)
      const formData = new FormData();

      // 2. Append text fields
      formData.append('action', isEditingProductId ? 'update_product' : 'add_product');

      // If updating, include the ID so Supabase knows which row to change
      if (isEditingProductId) {
        formData.append('product_id', isEditingProductId.toString());
      }

      formData.append('shop_id', shopId.toString());
      formData.append('shop_admin', user?.username || 'Admin');
      formData.append('product_name', newProduct.product_name);
      formData.append('product_category', newProduct.product_category);
      formData.append('product_description', newProduct.product_description);
      formData.append('base_price', newProduct.base_price);
      formData.append('stock_quantity', newProduct.stock_quantity);

      // 3. Append the binary file (The actual .jpg)
      if (selectedFile) {
        formData.append('product_image', selectedFile); 
      }

      // 4. Append variants as a JSON string
      if (hasVariants) {
        formData.append('variants', JSON.stringify(variants.map(v => ({
        ...v,
        variant_stock: parseInt(v.variant_stock.toString()) || 0
      }))));
    }

    // 5. Send to n8n (DO NOT set 'Content-Type' header manually)
    const response = await fetch('https://n8n.tenear.com/webhook/shop-products-add', {
      method: 'POST',
      body: formData // Browser sets boundary automatically
    });

    if (response.ok) {
      setShowAddForm(false);

      setIsEditingProductId(null); // IMPORTANT: Reset after success

      setSelectedFile(null);
      setImagePreview(null);
      setNewProduct({ product_name: '', product_category: '', product_description: '', base_price: '', stock_quantity: '0', product_image_url: '' });
      fetchProducts();
    }
  } catch (error) {
    alert("Error saving product.");
  } finally {
    setIsSubmitting(false);
  }
};
  
  const [isEditingProductId, setIsEditingProductId] = useState<number | null>(null);


  // ADD THIS FUNCTION
  const handleAmend = (product: any) => {
    console.log("Amending product:", product);

    // Set the ID first so the form knows it's an update
    setIsEditingProductId(product.product_id);

    // Your logic to open an edit modal or populate the form
    setNewProduct({
      product_name: product.product_name || '',
      product_category: product.product_category || '',
      product_description: product.product_description || '',
      base_price: (product.base_price ?? '').toString(),
      stock_quantity: (product.stock_quantity ?? '0').toString(),
      product_image_url: product.product_image_url || ''
    });

    // Populate variants if they exist
    if (product.variants && product.variants.length > 0) {
      setHasVariants(true);
      setVariants(product.variants);
    } else {
      setHasVariants(false);
      setVariants([]);
    }

    setShowAddForm(true);
  };


  const handleShare = async (product: any) => {
    // 1. Determine the source based on device capability
    // We'll default to 'whatsapp' for the desktop window.open, 
    // or 'system_share' for mobile.
    let source = "whatsapp"; 
  
    const trackShare = async (platform: string) => {
      try {
        await fetch('https://n8n.tenear.com/webhook/shop-products-management', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          action: 'share_product', 
          shop_id: shopId, 
          product_id: product.product_id,
          utm_source: platform, // Added tracking source
          timestamp: new Date().toISOString()
        })
      });
    } catch (error) {
      console.error("Failed to log share to n8n:", error);
    }
  };

  // 2. Construct URLs with the tracking parameter
  // This helps your Cloudflare Worker track clicks later too!
  const getShareUrl = (platform: string) => 
    `https://tenear.com/?shop_id=${shopId}&product_id=${product.product_id}&utm_source=${platform}`;

  const shareText = (url: string) => 
    `🤖 NEW PRODUCT ALERT!\n\n📦 ${product.product_name}\n💰 KShs ${product.base_price}\n🔗 View our online shop\n🔗 ${shopName}: ${url}`;

  // 3. Attempt Native Share (Mobile)
  if (typeof navigator.share !== 'undefined') {
    source = "mobile_native";
    const finalUrl = getShareUrl(source);
    try {
      await navigator.share({ 
        title: product.product_name, 
        text: shareText(finalUrl), 
        url: finalUrl 
      });
      await trackShare(source);
      return;
    } catch (err) {
      // If user cancels or it fails, fallback to WhatsApp
    }
  }

  // 4. Fallback for Desktop/WhatsApp
  source = "whatsapp_direct";
  const finalUrl = getShareUrl(source);
  await trackShare(source);
  window.open(`https://wa.me/?text=${encodeURIComponent(shareText(finalUrl))}`, '_blank');
};



  const handleDelete = async (id: number) => {
    if (window.confirm("Delete this product?")) {
      await fetch('https://n8n.tenear.com/webhook/shop-products-management', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'delete_product', product_id: id })
      });
      fetchProducts();
    }
  };

  return (
    <div className="p-6 bg-white rounded-xl shadow-sm border border-gray-100 max-w-6xl mx-auto min-h-[700px]">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Product Inventory</h2>
          <p className="text-gray-500 text-sm">Managing: <span className="font-bold text-blue-600 bg-blue-50 px-2 rounded">{shopName || `Shop ID: ${shopId}`}</span></p>
        </div>

        {/* 3. NEW: Centered Toggle Button & Right Actions */}
        <div className="flex items-center gap-3">

          {/* NEW WHATSAPP BUTTON */}
          <button
            onClick={fetchWhatsAppQR}
            disabled={isFetchingQr}
            className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-xl font-bold hover:bg-green-600 transition-all shadow-md disabled:opacity-50"
          >
            {isFetchingQr ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
            Link WhatsApp
          </button>

          <button 
            onClick={() => setShowAnalytics(!showAnalytics)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all border ${
              showAnalytics 
                ? "bg-blue-50 border-blue-200 text-blue-600" 
                : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"
            }`}
          >
            <BarChart3 className="w-4 h-4" />
            {showAnalytics ? "Close Analytics" : "View Analytics"}
          </button>

        <div className="flex gap-3">
          {!showAddForm && (
            <button onClick={() => setShowAddForm(true)} className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-all shadow-md">
              <Plus className="w-4 h-4" /> Add Product
            </button>
          )}
          <button onClick={onBack} className="flex items-center gap-2 text-gray-500 hover:text-blue-600 font-medium transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
        </div>
      </div>

      {/* 4. NEW: Conditionally render the Analytics Dashboard */}
      {showAnalytics && (
        <div className="mb-10 animate-in fade-in slide-in-from-top-4 duration-300">
           <ShopAnalytics shopId={shopId} />
        </div>
      )}
      </div>
      {/* Create Product Form */}
      {showAddForm && (
        <div className="mb-10 bg-gray-50 p-8 rounded-2xl border border-gray-200 shadow-inner">
          <form onSubmit={handleAddProduct} className="space-y-6">
            {/* Image Upload Area */}
            
            <div className="space-y-2">
              <label className="block text-sm font-bold text-gray-700">Upload Product Image</label>
              <div 
                onDragEnter={handleDrag} 
                onDragLeave={handleDrag} 
                onDragOver={handleDrag} 
                onDrop={handleDrop} 
                onClick={() => fileInputRef.current?.click()} // Triggers the hidden input
                className={`border-2 border-dashed rounded-xl p-8 flex flex-col items-center justify-center cursor-pointer transition-all ${
                  dragActive ? "border-blue-500 bg-blue-50" : "border-gray-300 hover:border-blue-400"
                }`}
             >
                {imagePreview ? (
                  <div className="relative w-full aspect-video">
                    <img src={imagePreview} className="w-full h-full object-cover rounded-lg" alt="Preview" />
                    <button 
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setSelectedFile(null); setImagePreview(null); }}
                      className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full shadow-lg"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <>
                    <ImageIcon className="w-12 h-12 text-gray-400 mb-2" />
                    <p className="text-sm text-gray-500">Drag & drop or click to upload .jpg</p>
                  </>
                )}
    
                {/* Hidden Input */}
                <input 
                  type="file" 
                  ref={fileInputRef} 
                  onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
                  className="hidden" 
                  accept="image/jpeg,image/png"
                />
              </div>
            </div>
             


            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <input required placeholder="Product Name" className="w-full px-4 py-3 rounded-xl border border-gray-300 outline-none" value={newProduct.product_name} onChange={(e) => setNewProduct({...newProduct, product_name: e.target.value})} />
              <input required placeholder="Category" className="w-full px-4 py-3 rounded-xl border border-gray-300 outline-none" value={newProduct.product_category} onChange={(e) => setNewProduct({...newProduct, product_category: e.target.value})} />
            </div>

            <textarea rows={3} placeholder="Description" className="w-full px-4 py-3 rounded-xl border border-gray-300 outline-none resize-none" value={newProduct.product_description} onChange={(e) => setNewProduct({...newProduct, product_description: e.target.value})} />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-medium">KShs</span>
                <input required type="number" placeholder="Base Price" className="w-full pl-16 pr-4 py-3 rounded-xl border border-gray-300 outline-none" value={newProduct.base_price} onChange={(e) => setNewProduct({...newProduct, base_price: e.target.value})} />
              </div>
              <input required type="number" placeholder="Initial Stock Quantity" className="w-full px-4 py-3 rounded-xl border border-gray-300 outline-none" value={newProduct.stock_quantity} onChange={(e) => setNewProduct({...newProduct, stock_quantity: e.target.value})} />
            </div>

            {/* Variant Switch */}
            <div className="pt-4 border-t">
              <label className="flex items-center gap-2 cursor-pointer mb-4">
                <input type="checkbox" checked={hasVariants} onChange={(e) => setHasVariants(e.target.checked)} className="w-5 h-5 rounded text-blue-600" />
                <span className="text-sm font-bold text-gray-700">Add variations (Size, Color, etc.)</span>
              </label>

              {hasVariants && (
                <div className="space-y-3 bg-white p-4 rounded-xl border">
                  {variants.map((variant) => (
                    <div key={variant.id} className="flex gap-3 items-center">
                      <input placeholder="Name" className="flex-1 p-2 border rounded-lg" value={variant.variant_name} onChange={(e) => updateVariant(variant.id, 'variant_name', e.target.value)} />
                      <input placeholder="Value" className="flex-1 p-2 border rounded-lg" value={variant.value} onChange={(e) => updateVariant(variant.id, 'value', e.target.value)} />
                      <input type="number" placeholder="+/- Price" className="w-24 p-2 border rounded-lg" value={variant.price_impact} onChange={(e) => updateVariant(variant.id, 'price_impact', parseFloat(e.target.value) || 0)} />
                      <input type="number" placeholder="Stock" className="w-24 p-2 border rounded-lg" value={variant.variant_stock} onChange={(e) => updateVariant(variant.id, 'variant_stock', parseInt(e.target.value) || 0)} />
                      <button type="button" onClick={() => removeVariantRow(variant.id)} className="p-2 text-red-500"><X className="w-4 h-4" /></button>
                    </div>
                  ))}
                  <button type="button" onClick={addVariantRow} className="text-sm text-blue-600 font-bold">+ Add Variant</button>
                </div>
              )}
            </div>

            <div className="flex gap-3">
              <button type="submit" disabled={isSubmitting} className="flex-1 bg-blue-600 text-white py-4 rounded-xl font-bold flex items-center justify-center gap-2">
                {isSubmitting ? <Loader2 className="animate-spin" /> : <Save />} Save & List Product
              </button>
              <button type="button" onClick={() => setShowAddForm(false)} className="px-6 py-4 rounded-xl border font-bold text-gray-600">Cancel</button>
            </div>
          </form>
        </div>
      )}

      {/* QR CODE MODAL (Add at the end of the return statement) */}
      {qrCode && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl p-8 max-w-sm w-full text-center shadow-2xl">
            <h2 className="text-2xl font-black mb-2">Link WhatsApp</h2>
            <p className="text-gray-500 text-sm mb-6">Scan this QR code with your phone to enable click alerts.</p>
      
            <div className="bg-gray-50 p-4 rounded-2xl mb-6 inline-block border-2 border-dashed border-gray-200">
              <img src={qrCode} alt="WhatsApp QR" className="w-64 h-64 mx-auto" />
            </div>

            <button 
              onClick={() => setQrCode(null)}
              className="w-full py-3 bg-gray-900 text-white rounded-xl font-bold hover:bg-gray-800 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Main Inventory Display Grid */}
      {isLoading ? (
        <div className="flex flex-col items-center justify-center py-20 gap-4">
          <Loader2 className="w-10 h-10 text-blue-600 animate-spin" />
          <p className="text-gray-500">Syncing with warehouse...</p>
        </div>
      ) : products.length === 0 ? (
        <div className="text-center py-20 bg-gray-50 rounded-2xl border border-dashed border-gray-300">
          <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-bold text-gray-900">No products found</h3>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Search Bar - Positioned above the grid */}
          <div className="relative max-w-md w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input 
              type="text" 
              placeholder="Search name or category..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 transition-all shadow-sm"
            />
          </div>

          {/* Product Grid - Using filteredProducts and passing actual functions */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProducts.map((product) => (
              <ProductCard
                key={product.product_id}
                product={product}
                handleShare={handleShare}
                handleDelete={handleDelete}
                handleAmend={handleAmend}
              />
            ))}
          </div>

          {/* Empty Search State */}
          {filteredProducts.length === 0 && (
            <div className="text-center py-10 bg-gray-50 rounded-xl border border-dashed">
              <p className="text-gray-400 italic">No products match your search "{searchTerm}"</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProductManager;
